/**
 * Dataset utility functions for OpenSearch model mapping and configuration
 */

/**
 * Map dataset names to OpenSearch model types
 */
export function getModelTypeFromDataset(dataset: string): string {
  switch (dataset) {
    case 'people':
      return 'Person';
    case 'companies':
      return 'Company';
    case 'tas-program-instances':
      return 'TasProgramInstance';
    case 'tas-program-templates':
      return 'TasProgramTemplate';
    case 'pslf-forms':
      return 'PslfForm';
    case 'syf-match-participants':
      return 'MatchParticipant';
    case 'syf-match-payments':
      return 'MatchPayment';
    case 'syf-match-participant-benefit-years':
      return 'MatchParticipantBenefitYear';
    case 'slr-contribution-plan-participants':
      return 'EmployeePlan';
    default:
      return 'Person';
  }
}

/**
 * Get the OpenSearch field name used for company filtering on a given dataset.
 * Returns undefined for datasets where company filtering is not applicable.
 */
export function getCompanyFilterField(dataset: string): string | undefined {
  switch (dataset) {
    case 'tas-program-instances':
    case 'tas-program-templates':
    case 'pslf-forms':
      return 'company_id';
    case 'people':
      return 'company_ids';
    case 'companies':
      return 'company_id_keyword';
    case 'syf-match-participants':
      return 'syf_match_participant_company_id_keyword';
    case 'syf-match-payments':
    case 'syf-match-participant-benefit-years':
      return 'company_id';
    case 'slr-contribution-plan-participants':
      return 'slr_contribution_plan_participant_company_id_keyword';
    default:
      return undefined;
  }
}

/**
 * Available datasets for reporting and charting
 */
export const AVAILABLE_DATASETS = [
  { key: 'people', label: 'People Data', modelType: 'Person' },
  { key: 'companies', label: 'Company Data', modelType: 'Company' },
  {
    key: 'tas-program-instances',
    label: 'Tuition Assistance Programs',
    modelType: 'TasProgramInstance',
  },
  {
    key: 'tas-program-templates',
    label: 'Tuition Assistance Program Templates',
    modelType: 'TasProgramTemplate',
  },
  { key: 'pslf-forms', label: 'PSLF Forms', modelType: 'PslfForm' },
  {
    key: 'syf-match-participants',
    label: 'SYF Match Participants',
    modelType: 'MatchParticipant',
  },
  {
    key: 'syf-match-payments',
    label: 'SYF Match Payments',
    modelType: 'MatchPayment',
  },
  {
    key: 'syf-match-participant-benefit-years',
    label: 'SYF Benefit Year Participation',
    modelType: 'MatchParticipantBenefitYear',
  },
  {
    key: 'slr-contribution-plan-participants',
    label: 'SLR Contribution Participants',
    modelType: 'EmployeePlan',
  },
];

/**
 * Time range options for filtering chart data
 */
export const TIME_RANGE_OPTIONS = [
  { key: 'all', label: 'All Data' },
  { key: '3m', label: 'Last 3 Months' },
  { key: '6m', label: 'Last 6 Months' },
  { key: '12m', label: 'Last 12 Months' },
  { key: '24m', label: 'Last 24 Months' },
  { key: '60m', label: 'Last 5 Years' },
];

/**
 * Get default sort fields for a dataset.
 * These are the OpenSearch field names (snake_case) used for ordering results.
 *
 * Note: Only use keyword-type fields for sorting. Text fields will cause
 * OpenSearch errors unless fielddata is enabled or a .keyword sub-field exists.
 * Use `_score:desc` when no reliable keyword sort field is available.
 */
export function getDefaultSortFields(dataset: string): string[] {
  switch (dataset) {
    case 'tas-program-instances':
      return ['tas_program_instance_created_at_date:desc', 'tas_application_created_at_date:desc'];
    case 'companies':
      return ['company_name_keyword:asc'];
    case 'pslf-forms':
    case 'tas-program-templates':
    case 'syf-match-participants':
    case 'slr-contribution-plan-participants':
    case 'syf-match-payments':
    case 'syf-match-participant-benefit-years':
    case 'people':
    default:
      // _score:desc is universally safe; _id tiebreaker is added by setSort()
      return ['_score:desc'];
  }
}

/**
 * Get essential fields that must always be included in query results for a dataset.
 * These are needed for routing, linking, and conditional logic in the table.
 */
export function getEssentialFields(dataset: string): string[] {
  switch (dataset) {
    case 'tas-program-instances':
      return [
        'tas_application_id_keyword',
        'tas_program_instance_id_keyword',
        'tas_program_template_id_keyword',
        'id',
        'tas_application_state_keyword',
        'tas_program_instance_state_keyword',
        'tas_application_created_at_date',
        'tas_program_instance_created_at_date',
      ];
    case 'pslf-forms':
      return ['id', 'form_id', 'form_status', 'form_type', 'company_id', 'created_at'];
    case 'people':
      return ['id', 'person_id_keyword', 'person_first_name_text', 'person_last_name_text'];
    case 'companies':
      return ['id', 'company_id_keyword', 'company_name_keyword', 'company_type_keyword'];
    case 'tas-program-templates':
      return ['id', 'company_id', 'created_at'];
    case 'syf-match-participants':
      return ['id', 'created_at'];
    case 'syf-match-payments':
      return ['id', 'created_at', 'company_id', 'employee_id'];
    case 'syf-match-participant-benefit-years':
      return ['id', 'created_at', 'company_id', 'employee_id'];
    case 'slr-contribution-plan-participants':
      return ['id', 'created_at'];
    default:
      return ['id', 'created_at'];
  }
}

/**
 * Get default columns to display for each dataset when the user hasn't selected any.
 * Returns snake_case field names matching OpenSearch.  An empty array means
 * "show all columns" (fall through to the existing first-item-key logic).
 *
 * TAS default columns are defined separately in tas-column-config.ts
 * (DEFAULT_COLUMNS.individual / DEFAULT_COLUMNS.aggregate).
 * The entry here is only used for individual/document mode defaults.
 */
export function getDefaultColumns(dataset: string): string[] {
  switch (dataset) {
    case 'tas-program-instances':
      // Individual mode defaults — matches DEFAULT_COLUMNS.individual in tas-column-config.ts.
      // Aggregate mode bypasses this entirely (see selectedColumnsArray in programs-page).
      return [
        'employee_name_keyword',
        'employee_payroll_id_keyword',
        'tas_program_template_field_program_name_keyword',
        'tas_program_instance_field_school_institution_name_keyword',
        'tas_application_id_keyword',
        'tas_program_instance_created_at_date',
      ];
    case 'companies':
      return [
        'company_name_keyword',
        'company_type_keyword',
        'company_is_active_boolean',
        'company_is_live_boolean',
        'company_products_enabled_keyword',
        'company_sub_domain_keyword',
        'company_launch_date_date',
        'company_partner_type_keyword',
        'company_created_at_date',
      ];
    case 'people':
      return [
        'person_first_name_text',
        'person_last_name_text',
        'all_employee_names_text',
        'all_emails',
        'all_company_names_keyword',
        'person_is_active_boolean',
        'person_is_enrolled_boolean',
        'all_employee_payroll_ids_text',
      ];
    case 'pslf-forms':
      return [
        'borrower_name',
        'employer_name',
        'form_type',
        'form_status',
        'created_at',
        'updated_at',
      ];
    case 'tas-program-templates':
      return [
        'tas_program_template_name_text',
        'tas_program_template_type_keyword_analyzable',
        'tas_program_template_company_name_keyword',
        'tas_program_template_state_keyword_analyzable',
        'tas_program_template_created_at_date',
      ];
    case 'syf-match-participants':
      return [
        'syf_match_participant_person_name_text',
        'syf_match_participant_enrollment_state_keyword',
        'syf_match_participant_plan_name_text',
        'syf_match_participant_created_at_date',
      ];
    case 'syf-match-payments':
      return [
        'syf_match_payment_transaction_date_date',
        'syf_match_participant_person_name_text',
        'syf_match_participant_company_name_keyword',
        'syf_match_participant_plan_code_keyword',
        'syf_match_participant_benefit_year_integer',
        'syf_match_payment_transaction_amount_integer',
        'syf_match_payment_payment_qualified_status_keyword',
        'syf_match_payment_payment_certification_status_keyword',
      ];
    case 'syf-match-participant-benefit-years':
      return [
        'syf_match_participant_benefit_year_person_name_text',
        'syf_match_participant_benefit_year_company_name_keyword',
        'syf_match_participant_benefit_year_plan_code_keyword',
        'syf_match_participant_benefit_year_integer',
        'syf_match_participant_benefit_year_total_payment_count_integer',
        'syf_match_participant_benefit_year_total_paid_amount_integer',
        'syf_match_participant_benefit_year_completion_bucket_keyword',
      ];
    case 'slr-contribution-plan-participants':
      return [
        'slr_contribution_plan_participant_person_name_text',
        'slr_contribution_plan_participant_enrollment_status_keyword',
        'slr_contribution_plan_participant_plan_name_text',
        'slr_contribution_plan_participant_created_at_date',
      ];
    default:
      // Empty = show all columns (existing behavior)
      return [];
  }
}
